import { Component, Input, Output, EventEmitter } from '@angular/core';

/**
 * Shared OEE Modal Component
 *
 * Enforces consistent modal structure across all OEE dialogs.
 * Use this component instead of building modal structure manually.
 *
 * SIZE GUIDE - Use these widths when opening the dialog:
 * - sm: 450px  - Simple dialogs (confirmations, Add System)
 * - md: 600px  - Standard forms (Log Downtime, Log Incident, Create Site)
 * - lg: 800px  - Complex forms (Edit Equipment, Edit Batch)
 * - xl: 1000px - Very complex dialogs (OEE Settings with tabs)
 *
 * @example
 * // Opening a medium-sized modal
 * this.dialog.open(MyModalComponent, { width: '600px' });
 *
 * // Template usage
 * <app-oee-modal
 *     title="Log Downtime"
 *     subtitle="Select a reason for the downtime"
 *     [contextName]="equipmentName"
 *     icon="fa-tools"
 *     iconColor="red"
 *     size="md">
 *
 *     <ng-container modal-content>
 *         <form>...</form>
 *     </ng-container>
 *
 *     <ng-container modal-actions>
 *         <button mat-button>Cancel</button>
 *         <button mat-raised-button color="primary">Submit</button>
 *     </ng-container>
 * </app-oee-modal>
 */
@Component({
    selector: 'app-oee-modal',
    templateUrl: './oee-modal.component.html',
    styleUrls: ['./oee-modal.component.scss'],
    standalone: false
})
export class OeeModalComponent {
    /** Modal title (e.g., "Log Downtime", "Start Batch") */
    @Input() title = '';

    /** Subtitle/description text below the title */
    @Input() subtitle = '';

    /** Context name displayed after the title (e.g., equipment name) - shown in highlight color */
    @Input() contextName = '';

    /**
     * Icon to display in the header.
     * For Font Awesome: just the icon name without 'fas' (e.g., "fa-tools", "fa-play")
     * For Material: the icon name (e.g., "play_arrow", "edit")
     */
    @Input() icon = '';

    /** Whether the icon is a Material icon (true) or Font Awesome (false) */
    @Input() useMaterialIcon = false;

    /** Icon background color theme */
    @Input() iconColor: 'blue' | 'green' | 'yellow' | 'red' | 'purple' = 'blue';

    /**
     * Background tint color - adds a subtle color tint to the modal background.
     * - 'none': Neutral warm gray (default)
     * - 'amber': Light amber/yellow tint (good for warnings/incidents)
     * - 'red': Light red tint (good for danger/errors)
     * - 'green': Light green tint (good for success)
     * - 'blue': Light blue tint (good for info)
     */
    @Input() tint: 'none' | 'amber' | 'red' | 'green' | 'blue' = 'none';

    /**
     * Modal size - adds a CSS class for size-specific styling.
     * Note: Dialog width is controlled by MatDialog config when opening.
     * Recommended widths: sm=450px, md=600px, lg=800px, xl=1000px
     */
    @Input() size: 'sm' | 'md' | 'lg' | 'xl' = 'md';

    /** Maximum height for the content area */
    @Input() contentMaxHeight = '65vh';

    /** Whether the modal is in a loading state (shows spinner instead of content) */
    @Input() loading = false;

    /** Loading message to display */
    @Input() loadingMessage = 'Loading...';

    /** Event emitted when close button is clicked */
    @Output() closeClick = new EventEmitter<void>();

    get iconClass(): string {
        if (this.useMaterialIcon) {
            return '';
        }
        // Font Awesome - ensure 'fas' prefix
        return `fas ${this.icon.startsWith('fa-') ? this.icon : 'fa-' + this.icon}`;
    }

    get iconColorClass(): string {
        return `icon-${this.iconColor}`;
    }

    get sizeClass(): string {
        return `size-${this.size}`;
    }

    get tintClass(): string {
        return this.tint !== 'none' ? `tint-${this.tint}` : '';
    }

    onClose(): void {
        this.closeClick.emit();
    }
}
